import { useCallback, useEffect, useRef, useState } from "react";
import { LuRefreshCw } from "react-icons/lu";
import axios from "../../axios";
import * as styled from "./TrialRequests.styled";
import * as form from "../../pages/AdminRoom/AddStudent/AddStudent.styled.jsx";
import { PersonPicker } from "../../pages/AdminRoom/AddStudent/AddStudent.jsx";
import BotLinkButton from "../BotLinkButton/BotLinkButton";
import { getAllTeachers } from "../../helpers/getAllTeachers";
import { secondStepFields } from "../../helpers/addTrialLesson/secondStepFields";

// «Шукаємо вчителя»: запити на пробний без вчителя (див. сторінку «Запит на пробний»).
// Вчителі відгукуються в боті → тут видно хто → «Призначити» створює звичайний пробний,
// після чого «🤖 Посилання на бот» — як завжди (шкільний бот видає доступ).
const GROUP_LABEL = { kids: "KIDS", teenagers: "TEENS", adults: "ADULTS" };
const DAYS = [
  { name: "Пн", value: "monday" },
  { name: "Вт", value: "tuesday" },
  { name: "Ср", value: "wednesday" },
  { name: "Чт", value: "thursday" },
  { name: "Пт", value: "friday" },
  { name: "Сб", value: "saturday" },
  { name: "Нд", value: "sunday" },
];
const LEVELS = secondStepFields.find(({ formName }) => formName === "englishLevel").selectOptions;

const generatePassword = () => {
  const chars = "abcdefghjkmnpqrstuvwxyz23456789";
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

const ago = (date) => {
  const minutes = Math.round((Date.now() - new Date(date)) / 60000);
  if (minutes < 60) return `${minutes} хв тому`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} год тому`;
  return new Date(date).toLocaleDateString("uk-UA", { day: "numeric", month: "short" });
};

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); }
  catch { const t = document.createElement("textarea"); t.value = text; document.body.appendChild(t); t.select(); document.execCommand("copy"); t.remove(); }
};

function AssignPanel({ request, teachers, onDone, onClose }) {
  const responders = request.responses || [];
  const [teacherId, setTeacherId] = useState(responders.find((r) => r.kind === "take")?.teacherId || "");
  const [day, setDay] = useState("");
  const [time, setTime] = useState("");
  const [englishLevel, setEnglishLevel] = useState("");
  const [login, setLogin] = useState({ studentId: "", email: "" });
  const [password, setPassword] = useState(generatePassword);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const suggestLogin = useCallback(() => {
    axios.get("trial-requests/login-suggestion", { params: { name: request.name } })
      .then(({ data }) => setLogin(data))
      .catch(() => {});
  }, [request.name]);

  useEffect(suggestLogin, [suggestLogin]);

  const submit = async () => {
    if (!teacherId) return setError("Оберіть вчителя");
    if (!day || !time) return setError("Вкажіть день і час пробного");
    if (!englishLevel) return setError("Оберіть рівень");
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post(`trial-requests/${request._id}/assign`, { teacherId, day, time, englishLevel, ...login, password });
      onDone(data.request);
    } catch (err) {
      setError(err?.response?.data?.message || "Не вдалося призначити");
      // ID або логін уже зайняті (хтось призначив інший пробний паралельно) — підставляємо вільні
      if (err?.response?.data?.field === "login") suggestLogin();
      setSaving(false);
    }
  };

  return (
    // form — щоб браузер запамʼятовував введені логіни й підказував їх
    <styled.Panel as="form" onSubmit={(event) => { event.preventDefault(); submit(); }}>
      {responders.length > 0 && (
        <form.Field>
          <form.Label>Відгукнулись</form.Label>
          <form.Chips>
            {responders.map((r) => (
              <form.Chip key={r.teacherId} type="button" $active={teacherId === r.teacherId} onClick={() => setTeacherId(r.teacherId)}>
                {r.kind === "take" ? "✋" : "🕐"} {r.teacherName}{r.teacherSchoolId ? ` · ID ${r.teacherSchoolId}` : ""}
              </form.Chip>
            ))}
          </form.Chips>
        </form.Field>
      )}
      <PersonPicker label={responders.length ? "Або інший вчитель" : "Вчитель"} people={teachers} value={teacherId} onChange={setTeacherId} />
      <form.Field>
        <form.Label>День і час пробного</form.Label>
        <form.Chips>
          {DAYS.map(({ name, value }) => (
            <form.Chip key={value} type="button" $active={day === value} onClick={() => setDay(value)}>{name}</form.Chip>
          ))}
        </form.Chips>
        <form.Input type="time" value={time} onChange={(event) => setTime(event.target.value)} />
      </form.Field>
      <form.Field>
        <form.Label>Рівень</form.Label>
        <form.Chips>
          {LEVELS.map(({ name, value }) => (
            <form.Chip key={value} type="button" $active={englishLevel === value} onClick={() => setEnglishLevel(value)}>{name}</form.Chip>
          ))}
        </form.Chips>
        {request.level && <styled.Muted>Зі слів клієнта: {request.level}</styled.Muted>}
      </form.Field>
      <form.Grid>
        <form.Field>
          <form.Label>Логін</form.Label>
          <form.Input name="email" value={login.email} placeholder="підбираємо…" onChange={(event) => setLogin((prev) => ({ ...prev, email: event.target.value.trim() }))} />
          <styled.Muted>Id {login.studentId || "…"} · бот однаково видасть людині новий пароль</styled.Muted>
        </form.Field>
        <form.Field>
          <form.Label>Пароль</form.Label>
          <form.InputRow>
            <form.Input value={password} autoComplete="new-password" onChange={(event) => setPassword(event.target.value)} />
            <form.IconButton type="button" title="Згенерувати" onClick={() => setPassword(generatePassword())}>
              <LuRefreshCw size={18} />
            </form.IconButton>
          </form.InputRow>
        </form.Field>
      </form.Grid>
      {error && <styled.Error>{error}</styled.Error>}
      <styled.Row>
        <styled.Button type="submit" $kind="primary" disabled={saving}>{saving ? "Призначаємо…" : "Призначити пробний"}</styled.Button>
        <styled.Button type="button" onClick={onClose}>Закрити</styled.Button>
      </styled.Row>
    </styled.Panel>
  );
}

function RequestCard({ request, teachers, onChange }) {
  const [assigning, setAssigning] = useState(false);
  const [busy, setBusy] = useState(false);
  const teacherName = (id) => {
    const t = teachers.find((x) => x._id === id);
    if (t) return `${t.name} (ID ${t.teacherId})`;
    const r = request.responses?.find((x) => x.teacherId === id);
    return r ? `${r.teacherName}${r.teacherSchoolId ? ` (ID ${r.teacherSchoolId})` : ""}` : "вчитель";
  };
  const tone = request.status === "assigned" ? "done" : request.status === "cancelled" ? "off" : "open";

  const cancel = async () => {
    if (!window.confirm(`Скасувати запит «${request.name}»? Вчителі побачать, що запит закрито.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`trial-requests/${request._id}/cancel`);
      onChange(data.request);
    } catch (err) {
      window.alert(err?.response?.data?.message || "Не вдалося скасувати");
      setBusy(false);
    }
  };

  return (
    <styled.Card $tone={tone}>
      <styled.CardTitle>
        <b>{request.name}{request.trialStudentId ? ` (ID ${request.trialStudentId})` : ""}{request.age ? `, ${request.age}` : ""} · {GROUP_LABEL[request.ageGroup] || request.ageGroup}</b>
        <small>{ago(request.createdAt)}</small>
      </styled.CardTitle>
      <styled.Facts>
        <dt>Пробний</dt><dd>{request.trialWhen}</dd>
        {request.lessonsPerWeek && <><dt>Регулярні</dt><dd>{request.lessonsPerWeek}</dd></>}
        {request.regularSchedule && <><dt>Графік</dt><dd>{request.regularSchedule}</dd></>}
        {request.level && <><dt>Рівень</dt><dd>{request.level}</dd></>}
        <dt>Телефон</dt><dd>{request.phoneNumber}</dd>
        {request.teacherComment && <><dt>Вчителям</dt><dd>{request.teacherComment}</dd></>}
        {request.notes && <><dt>Нотатки</dt><dd>{request.notes}</dd></>}
      </styled.Facts>

      {request.status === "searching" && (
        <styled.Responses>
          {request.responses?.length ? request.responses.map((r) => (
            <span key={r.teacherId}>
              {r.kind === "take" ? "✋" : "🕐"} <b>{r.teacherName}</b>{r.teacherSchoolId && <styled.Muted> ID {r.teacherSchoolId}</styled.Muted>} — {r.kind === "take" ? "бере" : r.note} <styled.Muted>· {ago(r.at)}</styled.Muted>
            </span>
          )) : <styled.Muted>Ще ніхто не відгукнувся · отримали в боті: {request.sent?.length || 0}</styled.Muted>}
        </styled.Responses>
      )}

      {request.status === "assigned" && (
        <>
          <span>✅ Призначено: <b>{teacherName(request.assignedTeacherId)}</b></span>
          {request.trialLessonId && (
            <styled.Row>
              <BotLinkButton kind="trial" id={request.trialLessonId} hint="Скопіюйте і надішліть людині — бот видасть логін і пароль" />
              <styled.Muted>надішліть людині — бот видасть доступ</styled.Muted>
            </styled.Row>
          )}
        </>
      )}
      {request.status === "cancelled" && <styled.Muted>🛑 Скасовано</styled.Muted>}

      {request.status === "searching" && (assigning ? (
        <AssignPanel request={request} teachers={teachers} onClose={() => setAssigning(false)} onDone={(updated) => { setAssigning(false); onChange(updated, true); }} />
      ) : (
        <styled.Row>
          <styled.Button type="button" $kind="primary" onClick={() => setAssigning(true)}>Призначити</styled.Button>
          <styled.Button type="button" $kind="danger" disabled={busy} onClick={cancel}>Скасувати</styled.Button>
        </styled.Row>
      ))}
    </styled.Card>
  );
}

function TeacherBotPanel({ accessType }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState("");
  useEffect(() => {
    axios.get("admin/teacher-bot/teachers", { params: { accessType } }).then(({ data }) => setData(data)).catch(() => setData({ teachers: [] }));
  }, [accessType]);

  const copyLink = async (teacher) => {
    try {
      const { data: link } = await axios.post("admin/teacher-bot/link", { teacherId: teacher._id });
      await copyText(link.url);
      setCopied(teacher._id);
      setTimeout(() => setCopied(""), 2500);
    } catch (err) {
      window.alert(err?.response?.data?.message || "Не вдалося створити посилання");
    }
  };

  if (!data) return <styled.Muted>Завантаження…</styled.Muted>;
  const linked = data.teachers.filter((t) => t.linked).length;
  return (
    <styled.TeacherList>
      <styled.Muted>
        {data.enabled ? `Підключили бота: ${linked} з ${data.teachers.length}. Посилання одноразове — надішліть вчителю, він натисне Start.` : "Бот вчителів вимкнений (немає токена на сервері)."}
      </styled.Muted>
      {data.teachers.map((t) => (
        <styled.TeacherRow key={t._id}>
          <span>{t.name} <styled.Muted>id {t.teacherId}</styled.Muted></span>
          <styled.Row>
            {t.linked ? <span style={{ color: "#17A06A", fontWeight: 700 }}>✅ підключено</span> : t.botBlocked ? <styled.Muted>заблокував бота</styled.Muted> : null}
            {data.enabled && (
              <styled.Button type="button" onClick={() => copyLink(t)}>{copied === t._id ? "✅ Скопійовано" : t.linked ? "🔗 Ще раз" : "🔗 Посилання"}</styled.Button>
            )}
          </styled.Row>
        </styled.TeacherRow>
      ))}
    </styled.TeacherList>
  );
}

export default function TrialRequests({ accessType, onAssigned }) {
  const [requests, setRequests] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [showClosed, setShowClosed] = useState(false);
  const [showBot, setShowBot] = useState(false);
  const [justAssigned, setJustAssigned] = useState([]);   // лишаємо нагорі, щоб одразу скопіювати посилання на бот

  const latest = useRef(0);
  const load = useCallback(() => {
    if (!accessType) return;
    // Відповідь старішого запиту (напр. ще без мови) не перезаписує свіжішу
    const n = ++latest.current;
    axios.get("trial-requests", { params: { accessType } })
      .then(({ data }) => { if (n === latest.current) setRequests(data.requests); })
      .catch(() => {});
  }, [accessType]);

  useEffect(() => {
    load();
    // Відгуки вчителів приходять з бота — підтягуємо, поки сторінка відкрита
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!accessType) return;
    getAllTeachers()
      .then((list) => setTeachers(list.filter((t) => accessType === "all" || t.accessType === accessType).filter((t) => !t.accessBlocked)))
      .catch(() => {});
  }, [accessType]);

  const onChange = (updated, assigned) => {
    setRequests((prev) => prev.map((r) => (r._id === updated._id ? { ...r, ...updated } : r)));
    if (assigned) {
      setJustAssigned((prev) => [...prev, updated._id]);
      onAssigned?.();
    }
  };

  const isOpen = (r) => r.status === "searching" || justAssigned.includes(r._id);
  const open = requests.filter(isOpen);
  const closed = requests.filter((r) => !isOpen(r));
  const waiting = requests.filter((r) => r.status === "searching").length;

  return (
    <styled.Wrap>
      <styled.Head>
        <h3>🔎 Шукаємо вчителя <styled.Count>{waiting}</styled.Count></h3>
        <styled.Row>
          {closed.length > 0 && (
            <styled.Button type="button" onClick={() => setShowClosed((v) => !v)}>{showClosed ? "Сховати закриті" : `Закриті за 14 днів (${closed.length})`}</styled.Button>
          )}
          <styled.Button type="button" onClick={() => setShowBot((v) => !v)}>🤖 Бот вчителів</styled.Button>
        </styled.Row>
      </styled.Head>
      {showBot && <TeacherBotPanel accessType={accessType} />}
      {open.length === 0 && !showClosed && <styled.Muted>Немає запитів, що чекають на вчителя.</styled.Muted>}
      <styled.Cards>
        {[...open, ...(showClosed ? closed : [])].map((r) => (
          <RequestCard key={r._id} request={r} teachers={teachers} onChange={onChange} />
        ))}
      </styled.Cards>
    </styled.Wrap>
  );
}
